/** Minimal RFC-4180-ish CSV parser for the user-import wizard. */

export interface CsvParseResult {
  /** Original header row, lower-cased + trimmed for case-insensitive lookup. */
  headers: string[];
  /** One object per data row, keyed by the normalised header names. */
  rows: Array<Record<string, string>>;
}

const parseRow = (line: string): string[] => {
  const out: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ',') {
      out.push(field);
      field = '';
      i += 1;
      continue;
    }
    field += ch;
    i += 1;
  }
  out.push(field);
  return out;
};

/**
 * Splits raw CSV text into logical rows, respecting quoted newlines.
 */
const splitRows = (text: string): string[] => {
  const rows: string[] = [];
  let buf = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') {
        buf += '""';
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      buf += ch;
      continue;
    }
    if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (buf.length > 0) {
        rows.push(buf);
        buf = '';
      }
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      continue;
    }
    buf += ch;
  }
  if (buf.length > 0) rows.push(buf);
  return rows;
};

export const parseCsv = (text: string): CsvParseResult => {
  const rawRows = splitRows(text);
  if (rawRows.length === 0) return { headers: [], rows: [] };
  const headers = parseRow(rawRows[0]).map((h) => h.trim().toLowerCase());
  const rows: Array<Record<string, string>> = [];
  for (let i = 1; i < rawRows.length; i += 1) {
    const cells = parseRow(rawRows[i]);
    if (cells.every((c) => c.trim() === '')) continue;
    const obj: Record<string, string> = {};
    headers.forEach((h, j) => {
      obj[h] = (cells[j] ?? '').trim();
    });
    rows.push(obj);
  }
  return { headers, rows };
};
